import React, { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useCoverEditor } from '../../CoverEditorContext';
import { coverEditorMessages, uploadErrorMessages } from '../../coverEditorMessages';
import { COVER_MAX_REFERENCES, type CoverReference } from './useCoverAiRun';
import { COVER_UPLOAD_TYPES, useCoverPictureUpload } from './useReferenceUpload';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AiReferencesProps {
  references: CoverReference[];
  onChange: (references: CoverReference[]) => void;
}

/**
 * The reference pictures of an AI request (design cover-editor b1, c1): up to 3 pictures that the
 * model follows. Each one uploads at once as a file of the artist.
 */
const AiReferencesComponent: React.FC<AiReferencesProps> = ({ references, onChange }) => {
  const intl = useIntl();
  const { accountId, readOnly } = useCoverEditor();
  const input = useRef<HTMLInputElement>(null);
  const { uploadPicture, isUploading, error } = useCoverPictureUpload(accountId);
  const pick = useCallback(() => input.current?.click(), []);
  const onFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // The same file can be picked again after a failure.
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
      if (file === undefined) {
        return;
      }
      uploadPicture(file)
        .then((saved) => {
          if (saved !== null) {
            onChange([...references, saved].slice(0, COVER_MAX_REFERENCES));
          }
        })
        .catch(console.error);
    },
    [onChange, references, uploadPicture]
  );
  const canAdd = references.length < COVER_MAX_REFERENCES && !readOnly;

  return (
    <div className={styles.column}>
      <div className={styles.references}>
        {references.map((reference) => (
          <span key={reference.fileId} className={styles.reference}>
            <img src={reference.url} alt="" />
            <button
              type="button"
              className={styles.referenceRemove}
              aria-label={intl.formatMessage(coverEditorMessages.referenceRemove)}
              onClick={() => onChange(references.filter((other) => other.fileId !== reference.fileId))}
            >
              ×
            </button>
          </span>
        ))}
        {canAdd && (
          <button
            type="button"
            className={styles.referenceAdd}
            aria-label={intl.formatMessage(coverEditorMessages.referenceAdd)}
            disabled={isUploading}
            onClick={pick}
          >
            {isUploading ? '…' : '+'}
          </button>
        )}
        <span className={styles.hint}>
          {intl.formatMessage(coverEditorMessages.referenceHint, { count: COVER_MAX_REFERENCES })}
        </span>
      </div>
      {error !== null && (
        <span className={styles.errorText} role="alert">
          {intl.formatMessage(uploadErrorMessages[error])}
        </span>
      )}
      <input
        ref={input}
        type="file"
        accept={COVER_UPLOAD_TYPES.join(',')}
        hidden
        aria-label={intl.formatMessage(coverEditorMessages.referenceAdd)}
        onChange={onFile}
      />
    </div>
  );
};

export const AiReferences = TypedMemo(AiReferencesComponent);
